/**
 * JanelaDaLigacao — a ligação do chat como JANELINHA (Task 11, 24/10/2026).
 *
 * A queixa que abriu o pedido: a ligação de voz tomava a tela inteira. Aqui a voz é uma janelinha pequena que
 * fica por cima do que a pessoa estiver fazendo; o vídeo reaproveita a `JanelaDeChamada` (tela cheia com
 * minimizar) SÓ depois que alguém atende. O desenho (`docs/superpowers/specs/2026-10-22-ligacoes-do-chat-tela.md`,
 * §2) é o contrato dos estados, dos textos e dos `data-testid`; as POSIÇÕES e o z-index do chip no celular foram
 * substituídos pelo §11 de `docs/superpowers/specs/2026-10-25-ligacoes-sobras-tela.md` (o chip mora numa faixa do
 * cabeçalho do app).
 *
 * ## A regra de ouro: a sala nunca muda de lugar na árvore
 *
 * Voz: do `chamando` ao fim do `em_curso` o `<InlineCallRoom>` é o MESMO elemento, na mesma posição; só as
 * classes e os textos em volta mudam (desmontar a sala derruba a ligação — ver `JanelaDeChamada`). Quem liga
 * por voz já entra na sala no `chamando`: quando o outro atende, o áudio está pronto. A sala vai escondida
 * (voz não tem palco) e uma `PonteDaSala` devolve o microfone, a conexão e a contagem de gente.
 *
 * Vídeo: em `chamando` (quem liga) e em `terminou` é a MESMA janelinha pequena da voz, sem sala e sem câmera;
 * a `JanelaDeChamada` monta em `em_curso` e fica até o fim. Ninguém se vê antes de a ligação existir.
 *
 * ## A sala que cai
 *
 * Ao encerrar, o servidor apaga a sala no LiveKit ANTES de mandar o `chamada:encerrada`: a sala de quem
 * ligou cai primeiro. Se isso desligasse na hora, a janelinha fecharia sem o "Juarez não atendeu" — a
 * mensagem que o Augusto pediu. Por isso a queda da sala espera `FOLGA_DO_EVENTO_MS` pelo evento (que troca
 * a fase e cancela a espera); se ele não vier, aí sim desliga. O botão vermelho desliga NA HORA.
 *
 * ## O erro passa
 *
 * O erro ocupa a linha de estado (52 px, uma linha só) e não pode ficar para sempre no lugar do cronômetro: fica 8 s
 * (`useErroTemporario`) ou sai antes, quando a causa passou — "Microfone bloqueado" quando o microfone liga,
 * "Falha na conexão" quando a sala volta a conectada depois de uma queda. O botão do microfone desligado é o aviso
 * que fica.
 *
 * ## O foco de quem liga
 *
 * Quem liga clicou num botão que sai de cena (o "Voz" do "Ligar para…", o 📞 que fica desligado, o "Ligar de volta"):
 * o foco cairia no `body` e quem usa teclado ou leitor de tela perderia o lugar. A janelinha foca a própria raiz ao
 * nascer, SÓ para quem liga (`chamando`); quem atende continua com o foco onde estava. O leitor anuncia o
 * `aria-label` da fase ("Chamando Juarez Costa, diálogo").
 *
 * ## Janelinha em cima de formulário
 *
 * A janelinha é um portal do `CallProvider`, fora da árvore React de qualquer Dialog/Sheet aberto: sem parar o
 * `pointerdown` o Radix toma o clique por "clicou fora" e fecha o formulário que a pessoa está preenchendo.
 *
 * O cronômetro é o `DuracaoDaChamada`: nada neste componente re-renderiza por tempo.
 */
import {
  AlertCircle,
  Loader2,
  Mic,
  MicOff,
  PhoneOff,
  Video as IconeDeVideo,
  X,
} from "lucide-react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { KeyboardEvent as EventoDeTecla, PointerEvent as EventoDePonteiro } from "react";
import { createPortal } from "react-dom";

import { UserAvatar } from "@/components/common/UserAvatar";
import { Button } from "@/components/ui/button";
import type { LigacaoAtual } from "@/contexts/CallContext";
import { useAPartirDe } from "@/hooks/use-mobile";
import { useErroTemporario } from "@/hooks/useErroTemporario";
import { aguardandoAlguemEntrar } from "@/lib/chamadas/aguardandoAlguem";
import { ehVideoAtendido } from "@/lib/chamadas/chip";
import {
  LIMIAR_DO_ARRASTO,
  PASSO_DO_TECLADO,
  posicaoArrastada,
  posicaoDoCanto,
  proximoCanto,
  type Canto,
  type Ponto,
} from "@/lib/chamadas/arrastar";
import { cn } from "@/lib/utils";

import { DuracaoDaChamada } from "./DuracaoDaChamada";
import { InlineCallRoom } from "./InlineCallRoom";
import { JanelaDeChamada, type ModoDaJanela } from "./JanelaDeChamada";
import { PonteDaSala, type EstadoDaSala } from "./PonteDaSala";

export interface JanelaDaLigacaoProps {
  ligacao: LigacaoAtual;
  /** A ficha da sala (token e URL do LiveKit). `null` até o `/initiate` ou o `join` responder. */
  sala: { token: string; wsUrl: string } | null;
  /** Desligar (cancelar, em `chamando`). Idempotente do lado do provider. */
  onDesligar: () => void;
  /** O X de `terminou`: o provider zera a ligação, o mesmo que o temporizador de 3 s faz. */
  onFechar: () => void;
  /**
   * Quem liga, ainda em `chamando`, vê 2 ou mais na sala de voz. Pode ser quem atendeu (e o `chamada:atendida`
   * pode ter se perdido) ou um supervisor entrando mudo: o provider confere o `/estado` e decide. Chamada uma vez
   * por mudança na contagem, nunca por tempo.
   */
  onAlguemEntrou?: () => void;
}

/** O ponto de corte celular × desktop do projeto (`useIsMobile`, o botão "Menu"): só `md` decide layout. */
const LARGURA_DO_DESKTOP = 768;
/** O tamanho da janelinha no desktop (`md:w-80 md:h-16`), para prender o arrasto na tela. */
const JANELA = { w: 320, h: 64 };
const MARGEM_DA_TELA = 12;
/** Shift + seta: um deslocamento que passa de qualquer tela, para a `posicaoArrastada` prender na borda. */
const ATE_A_BORDA = 100_000;
/** Quanto a queda da sala espera o `chamada:encerrada` antes de desligar por conta própria. */
const FOLGA_DO_EVENTO_MS = 2_000;
/** Os textos de erro que têm uma causa a acompanhar (os dois vêm de `PonteDaSala` e `SalaDeVoz`). */
const ERRO_DO_MICROFONE = "Microfone bloqueado";
const ERRO_DA_CONEXAO = "Falha na conexão";

const tamanhoDaTela = () => ({ w: window.innerWidth, h: window.innerHeight });

/** Onde a janela fica quando está no `canto` (0 = onde nasce: `right-4 bottom-24`, acima do composer). */
const posicaoNoCanto = (canto: Canto): Ponto =>
  posicaoDoCanto(canto, JANELA, tamanhoDaTela(), MARGEM_DA_TELA);

/** Quanto cada seta move a janela; `null` = não é seta. Shift leva até a borda daquele lado. */
function deslocamentoDaSeta(tecla: string, ateABorda: boolean): { dx: number; dy: number } | null {
  const passo = ateABorda ? ATE_A_BORDA : PASSO_DO_TECLADO;
  switch (tecla) {
    case "ArrowLeft":
      return { dx: -passo, dy: 0 };
    case "ArrowRight":
      return { dx: passo, dy: 0 };
    case "ArrowUp":
      return { dx: 0, dy: -passo };
    case "ArrowDown":
      return { dx: 0, dy: passo };
    default:
      return null;
  }
}

function rotuloDaJanela(ligacao: LigacaoAtual): string {
  if (ligacao.fase === "terminou") return "Ligação encerrada";
  if (ligacao.fase === "chamando") return `Chamando ${ligacao.titulo}`;
  const tipo = ligacao.midia === "video" ? "vídeo" : "voz";
  const grupo = ligacao.roomType === "GROUP" || ligacao.roomType === "SECTOR_AUTO";
  return `Ligação de ${tipo} ${grupo ? "em" : "com"} ${ligacao.titulo}`;
}

/* ── a sala de voz ───────────────────────────────────────────────────────────────────────────────── */

interface SalaDeVozProps {
  sala: { token: string; wsUrl: string };
  onDesligar: () => void;
  onEstado: (estado: EstadoDaSala | null) => void;
  onErro: (mensagem: string) => void;
}

/**
 * A sala de voz escondida (1 px, invisível, fora do leitor de tela e do clique) com a ponte dentro. Existe
 * como componente próprio para que a espera da queda da sala pertença a ELA: sai junto com a sala (o pai a
 * remonta por `key` a cada ligação), e a queda provocada pelo próprio desmonte não conta — senão uma sala
 * velha, ao ser desligada, desligaria a ligação seguinte.
 */
function SalaDeVoz({ sala, onDesligar, onEstado, onErro }: SalaDeVozProps) {
  const desligarRef = useRef(onDesligar);
  desligarRef.current = onDesligar;
  const espera = useRef<ReturnType<typeof setTimeout> | null>(null);
  const desmontada = useRef(false);

  useEffect(
    () => () => {
      // O desmonte desconecta a sala e isso dispara `onDisconnected` DEPOIS desta limpeza (ou antes): nos
      // dois casos nada fica agendado.
      desmontada.current = true;
      if (espera.current) clearTimeout(espera.current);
      espera.current = null;
    },
    []
  );

  const aoCair = useCallback(() => {
    if (desmontada.current) return;
    if (espera.current) clearTimeout(espera.current);
    espera.current = setTimeout(() => {
      espera.current = null;
      desligarRef.current();
    }, FOLGA_DO_EVENTO_MS);
  }, []);

  const aoFalhar = useCallback(
    (mensagem: string) => {
      // A mensagem do LiveKit é inglesa e longa: vai para o console; a janelinha mostra um texto curto fixo.
      console.warn("[JanelaDaLigacao] falha na sala de voz:", mensagem);
      onErro(ERRO_DA_CONEXAO);
    },
    [onErro]
  );

  return (
    <div
      aria-hidden
      className="pointer-events-none absolute -z-10 h-px w-px overflow-hidden opacity-0"
    >
      <InlineCallRoom
        token={sala.token}
        wsUrl={sala.wsUrl}
        video={false}
        mostrarControles={false}
        compacto
        onEnd={onDesligar}
        onDisconnected={aoCair}
        onErro={aoFalhar}
      >
        <PonteDaSala onEstado={onEstado} onErro={onErro} />
      </InlineCallRoom>
    </div>
  );
}

/* ── a janelinha ─────────────────────────────────────────────────────────────────────────────────── */

export function JanelaDaLigacao({
  ligacao,
  sala,
  onDesligar,
  onFechar,
  onAlguemEntrou,
}: JanelaDaLigacaoProps) {
  const [modoDoVideo, setModoDoVideo] = useState<ModoDaJanela>("cheia");
  const { erro, definirErro, limparErro } = useErroTemporario();
  const [estadoDaSala, setEstadoDaSala] = useState<EstadoDaSala | null>(null);
  const desktop = useAPartirDe(LARGURA_DO_DESKTOP);

  // O erro é da sala; uma ligação nova começa sem ele.
  useEffect(() => {
    limparErro();
  }, [ligacao.callSessionId, limparErro]);

  // O erro sai antes dos 8 s quando a causa passa. O texto do erro vai por `ref` para os efeitos olharem só a
  // MUDANÇA da sala (o microfone que liga, a conexão que volta), nunca o erro chegando com a sala já assim.
  const erroRef = useRef(erro);
  erroRef.current = erro;
  const microfoneLigado = estadoDaSala?.microfoneLigado;
  const conectada = estadoDaSala?.conectada;
  const reconectando = estadoDaSala?.reconectando;
  const caiu = useRef(false);

  useEffect(() => {
    if (microfoneLigado === true && erroRef.current === ERRO_DO_MICROFONE) limparErro();
  }, [microfoneLigado, limparErro]);

  useEffect(() => {
    if (conectada === undefined) {
      caiu.current = false; // a sala saiu do ar: a queda dela não vale para a próxima
      return;
    }
    if (!conectada || reconectando) {
      caiu.current = true;
      return;
    }
    if (!caiu.current) return;
    caiu.current = false;
    if (erroRef.current === ERRO_DA_CONEXAO) limparErro();
  }, [conectada, reconectando, limparErro]);

  // Quem liga ainda chama e a sala já tem outra pessoa: o `chamada:atendida` pode ter se perdido numa piscada
  // do sino. Quem decide é o servidor (o provider lê o `/estado`); aqui só se avisa, UMA vez por mudança na
  // contagem. O callback vai por `ref` para que um provider que re-renderiza não repita o aviso.
  const onAlguemEntrouRef = useRef(onAlguemEntrou);
  onAlguemEntrouRef.current = onAlguemEntrou;
  const quemLigaAindaChama = ligacao.fase === "chamando" && ligacao.souQuemLigou;
  const naSala = estadoDaSala?.participantes ?? 0;
  useEffect(() => {
    if (quemLigaAindaChama && naSala >= 2) onAlguemEntrouRef.current?.();
  }, [quemLigaAindaChama, naSala]);

  // O foco de quem liga (A7): só ao NASCER (o provider remonta a janelinha a cada ligação, por `key`). O valor do
  // primeiro render fica em `useState` para o efeito rodar uma vez só: quando alguém atende e a fase muda, a janelinha
  // não puxa o foco de volta de onde a pessoa estiver. `preventScroll`: a janelinha é fixa, a página não deve rolar.
  const raiz = useRef<HTMLDivElement>(null);
  const [nasceuChamando] = useState(quemLigaAindaChama);
  useEffect(() => {
    if (nasceuChamando) raiz.current?.focus({ preventScroll: true });
  }, [nasceuChamando]);

  /* Mudar de lugar (só no desktop), de três jeitos: ARRASTAR com o ponteiro (Pointer Events no elemento raiz:
     acompanha o ponteiro, sem inércia nem "imã"); SETAS no teclado (24 px, Shift até a borda); e, para quem não
     arrasta (WCAG 2.5.7), UM clique ou Enter/Espaço = o PRÓXIMO CANTO de um ciclo de 3 (o de cima à direita fica
     de fora: avisos de chamada e X dos painéis). A posição NÃO é lembrada entre ligações (toda ligação nasce no
     canto 0); sobrevive à troca de página porque o provider fica montado. */
  const idDaDica = useId();
  // O canto ESCOLHIDO (Enter/clique); começa em 0 e seta ou arrasto não o mudam: o próximo Enter/clique vai ao
  // canto seguinte ao último escolhido, venha a janela de onde vier.
  const [cantoAtual, setCantoAtual] = useState<Canto>(0);
  const [posicao, setPosicao] = useState<Ponto>(() => posicaoNoCanto(0));
  const arrasto = useRef<{ x: number; y: number; origem: Ponto; arrastando: boolean } | null>(null);
  // A janela saiu do canto escolhido (arrasto ou seta): no `resize` ela é só re-presa, não volta ao canto.
  const foraDoCanto = useRef(false);

  useEffect(() => {
    // A tela mudou: quem foi arrastada ou empurrada é re-presa (a tela que encolhe não deixa a janela para
    // fora); quem está num canto escolhido (ou nunca saiu do lugar, no 0) segue o canto.
    const aoRedimensionar = () =>
      setPosicao((atual) =>
        foraDoCanto.current
          ? posicaoArrastada(atual, { dx: 0, dy: 0 }, JANELA, tamanhoDaTela(), MARGEM_DA_TELA)
          : posicaoNoCanto(cantoAtual)
      );
    window.addEventListener("resize", aoRedimensionar);
    return () => window.removeEventListener("resize", aoRedimensionar);
  }, [cantoAtual]);

  const irParaOProximoCanto = () => {
    const proximo = proximoCanto(cantoAtual);
    foraDoCanto.current = false;
    setCantoAtual(proximo);
    setPosicao(posicaoNoCanto(proximo));
  };

  const aoApertar = (evento: EventoDePonteiro<HTMLDivElement>) => {
    // A janelinha é um portal FORA da árvore React de qualquer Dialog/Sheet aberto: sem isto o Radix toma o aperto
    // por "clicou fora" e fecha o formulário que a pessoa estava preenchendo (o mesmo cuidado do `AvisoDeChamada`).
    // O React escuta no contêiner do portal (`document.body`), então parar aqui impede o ouvinte do `document`. Antes
    // de qualquer `return`: vale no celular, nos botões e fora do arrasto.
    evento.stopPropagation();
    if (!desktop) return;
    if (evento.pointerType === "mouse" && evento.button !== 0) return;
    // Os botões continuam clicáveis: o arrasto (e o clique = próximo canto) os ignora.
    if ((evento.target as HTMLElement).closest("button")) return;
    arrasto.current = { x: evento.clientX, y: evento.clientY, origem: posicao, arrastando: false };
    evento.currentTarget.setPointerCapture?.(evento.pointerId); // o happy-dom não tem
  };

  const aoMover = (evento: EventoDePonteiro<HTMLDivElement>) => {
    const comeco = arrasto.current;
    if (!comeco) return;
    const dx = evento.clientX - comeco.x;
    const dy = evento.clientY - comeco.y;
    // Até o limiar é um clique que tremeu: a janela nem se mexe. Passou dele, é arrasto até soltar, mesmo que o
    // ponteiro volte ao ponto de partida.
    if (!comeco.arrastando && Math.hypot(dx, dy) < LIMIAR_DO_ARRASTO) return;
    comeco.arrastando = true;
    foraDoCanto.current = true;
    setPosicao(
      posicaoArrastada(comeco.origem, { dx, dy }, JANELA, tamanhoDaTela(), MARGEM_DA_TELA)
    );
  };

  /** Fim do gesto; devolve se foi um clique (não passou do limiar e não terminou em um botão). */
  const encerrarGesto = (evento: EventoDePonteiro<HTMLDivElement>): boolean => {
    const comeco = arrasto.current;
    if (!comeco) return false;
    arrasto.current = null;
    evento.currentTarget.releasePointerCapture?.(evento.pointerId);
    return !comeco.arrastando && !(evento.target as HTMLElement).closest("button");
  };

  const aoSoltar = (evento: EventoDePonteiro<HTMLDivElement>) => {
    if (encerrarGesto(evento)) irParaOProximoCanto();
  };

  // O sistema tomou o ponteiro (rolagem, chamada): o gesto acaba sem virar clique.
  const aoCancelar = (evento: EventoDePonteiro<HTMLDivElement>) => {
    encerrarGesto(evento);
  };

  const aoTeclar = (evento: EventoDeTecla<HTMLDivElement>) => {
    if (!desktop) return;
    // Só com a PRÓPRIA janela focada: Enter/Espaço/setas dos botões (e de qualquer filho) seguem com eles.
    if (evento.target !== evento.currentTarget) return;
    // Alt + ← é "voltar" do navegador, Ctrl/Meta têm os atalhos deles: não são da janela.
    if (evento.altKey || evento.ctrlKey || evento.metaKey) return;
    const seta = deslocamentoDaSeta(evento.key, evento.shiftKey);
    if (seta) {
      evento.preventDefault();
      foraDoCanto.current = true;
      setPosicao((atual) => posicaoArrastada(atual, seta, JANELA, tamanhoDaTela(), MARGEM_DA_TELA));
      return;
    }
    if (evento.key === "Enter" || evento.key === " ") {
      evento.preventDefault(); // o Espaço rolaria a página
      if (!evento.repeat) irParaOProximoCanto(); // segurar a tecla não gira pelos cantos
    }
  };

  // Vídeo atendido: a casca é a `JanelaDeChamada` (tela cheia com minimizar), `modo` local começando "cheia".
  if (ehVideoAtendido(ligacao)) {
    return (
      <JanelaDeChamada
        chamada={
          sala
            ? { callSessionId: ligacao.callSessionId, token: sala.token, wsUrl: sala.wsUrl }
            : null
        }
        startedAt={ligacao.iniciadaEm}
        titulo={ligacao.titulo}
        modo={modoDoVideo}
        onModoChange={setModoDoVideo}
        onEnd={onDesligar}
      />
    );
  }

  const terminou = ligacao.fase === "terminou";
  const grupo = ligacao.roomType === "GROUP" || ligacao.roomType === "SECTOR_AUTO";
  // A sala de voz só existe com a ficha E o id (a `key` da sala): sem eles a janelinha ainda não tem som.
  const salaDeVoz =
    ligacao.midia === "audio" && !terminou && sala !== null && ligacao.callSessionId !== null
      ? { idDaLigacao: ligacao.callSessionId, sala }
      : null;
  const aguardando = aguardandoAlguemEntrar(ligacao);

  return createPortal(
    <div
      ref={raiz}
      role="dialog" // não modal, como a `JanelaDeChamada`: uma região com controles
      aria-label={rotuloDaJanela(ligacao)}
      data-testid="janela-da-ligacao"
      data-fase={ligacao.fase}
      data-midia={ligacao.midia}
      data-canto={cantoAtual}
      // Desktop: entra na ordem do Tab (raiz → microfone → desligar) e as teclas movem a janela. Celular: o chip é
      // fixo, fica fora do Tab, mas continua focável por código (`focus()`).
      tabIndex={desktop ? 0 : -1}
      aria-describedby={desktop ? idDaDica : undefined}
      className={cn(
        "pointer-events-auto fixed flex items-center gap-2 rounded-xl border border-border bg-card text-card-foreground shadow-xl",
        // celular: DENTRO da faixa que o `Header` reserva para ele (60 px logo abaixo da linha do cabeçalho; `top-16` =
        // 4 px de folga), de borda a borda; ABAIXO da gaveta do menu e do painel de thread (z-40). Sem
        // `env(safe-area-inset-top)`: o chip segue o cabeçalho, que não o tem (desenho das sobras §11.3)
        "z-[35] inset-x-3 top-16 h-[52px] px-2",
        // desktop: canto inferior direito (a posição vem do `transform`), arrastável, na camada da janela de vídeo
        "md:z-50 md:inset-auto md:left-0 md:top-0 md:h-16 md:w-80 md:px-3 md:cursor-grab md:active:cursor-grabbing md:touch-none md:select-none",
        "animate-in fade-in-0 zoom-in-95 duration-200 motion-reduce:animate-none",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      )}
      style={desktop ? { transform: `translate3d(${posicao.x}px, ${posicao.y}px, 0)` } : undefined}
      onPointerDown={aoApertar}
      onPointerMove={aoMover}
      onPointerUp={aoSoltar}
      onPointerCancel={aoCancelar}
      onKeyDown={aoTeclar}
    >
      {/* ⚠️ POSIÇÃO FIXA NA ÁRVORE. Voz: o mesmo elemento em chamando e em_curso; só as classes e os textos em
          volta mudam. Em vídeo esta janelinha NUNCA monta sala. */}
      {salaDeVoz && (
        <SalaDeVoz
          key={salaDeVoz.idDaLigacao}
          sala={salaDeVoz.sala}
          onDesligar={onDesligar}
          onEstado={setEstadoDaSala}
          onErro={definirErro}
        />
      )}

      {/* Avatar só no desktop: no celular a coluna de texto precisa dos 44 px dele (o sinal de "chamando" lá é
          o ponto pulsante da linha de estado). O `UserAvatar` fixa o tamanho por `style`, não por classe. */}
      <div className="relative hidden shrink-0 md:block">
        {ligacao.fase === "chamando" && (
          <span
            aria-hidden
            className="absolute inset-0 rounded-full bg-primary/30 animate-ping motion-reduce:hidden"
          />
        )}
        <UserAvatar
          userId={ligacao.outroLadoId}
          name={ligacao.titulo}
          size={36}
          decorativo
          className={cn("relative", terminou && "opacity-60")}
        />
      </div>

      <div className="min-w-0 flex-1">
        <div title={ligacao.titulo} className="truncate text-sm font-semibold leading-tight">
          {ligacao.titulo}
        </div>
        {terminou ? (
          <div
            role="status"
            aria-live="polite"
            data-testid="aviso-da-ligacao"
            className="truncate text-xs text-muted-foreground"
          >
            {ligacao.aviso ?? "Chamada encerrada"}
          </div>
        ) : erro ? (
          // Erro é crítico para a pessoa: `role="alert"` (anuncia ao entrar no DOM), texto em `foreground` e
          // só o ícone vermelho (no escuro o `destructive` é cor de FUNDO, 1,8:1 como texto).
          <div
            role="alert"
            data-testid="erro-da-ligacao"
            className="flex min-w-0 items-center gap-1 text-xs font-medium text-foreground"
          >
            <AlertCircle
              className="h-3.5 w-3.5 shrink-0 text-destructive dark:text-red-400"
              aria-hidden
            />
            <span className="truncate">{erro}</span>
          </div>
        ) : (
          <div
            data-testid="estado-da-ligacao"
            className="flex min-w-0 items-center gap-1.5 text-xs tabular-nums text-muted-foreground"
          >
            {ligacao.fase === "chamando" && (
              <span
                aria-hidden
                className="h-2 w-2 shrink-0 rounded-full bg-primary animate-pulse motion-reduce:animate-none"
              />
            )}
            {ligacao.midia === "video" && (
              <IconeDeVideo className="h-3.5 w-3.5 shrink-0" aria-hidden />
            )}
            {ligacao.fase === "em_curso" && !estadoDaSala?.conectada ? (
              <>
                <Loader2
                  className="h-3.5 w-3.5 shrink-0 animate-spin motion-reduce:animate-none"
                  aria-hidden
                />
                <span className="truncate">
                  {estadoDaSala?.reconectando ? "Reconectando…" : "Conectando…"}
                </span>
              </>
            ) : ligacao.fase === "em_curso" ? (
              // Sem `aria-live`: um cronômetro vivo falaria a cada segundo.
              <span className="truncate">
                <DuracaoDaChamada startedAt={ligacao.iniciadaEm} />
                {grupo && ` · ${estadoDaSala?.participantes ?? 1} na ligação`}
              </span>
            ) : (
              <span className="truncate">
                {aguardando ? "Aguardando alguém entrar…" : "Chamando…"}
              </span>
            )}
          </div>
        )}
      </div>

      {/* O tamanho do ícone dentro de um `Button` vai no `className` do botão (`[&_svg]:size-5`): a base do
          `Button` tem `[&_svg]:size-3 sm:[&_svg]:size-4`, que vence um `h-5 w-5` no svg. */}
      <div className="flex shrink-0 items-center gap-1">
        {salaDeVoz && (
          <Button
            type="button"
            variant="secondary"
            size="icon"
            data-testid="microfone-da-ligacao"
            aria-label={
              (estadoDaSala?.microfoneLigado ?? true) ? "Desligar microfone" : "Ligar microfone"
            }
            data-ligado={(estadoDaSala?.microfoneLigado ?? true) ? "true" : "false"}
            className={cn(
              "h-10 w-10 rounded-full [&_svg]:size-5 sm:[&_svg]:size-5",
              estadoDaSala?.microfoneLigado === false && "text-destructive dark:text-red-400"
            )}
            onClick={() => estadoDaSala?.alternarMicrofone()}
          >
            {(estadoDaSala?.microfoneLigado ?? true) ? <Mic aria-hidden /> : <MicOff aria-hidden />}
          </Button>
        )}
        {terminou ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            data-testid="fechar-janela-da-ligacao"
            aria-label="Fechar"
            className="h-10 w-10 rounded-full [&_svg]:size-5 sm:[&_svg]:size-5"
            onClick={onFechar}
          >
            <X aria-hidden />
          </Button>
        ) : (
          <Button
            type="button"
            variant="destructive"
            size="icon"
            data-testid="desligar-ligacao"
            aria-label={ligacao.fase === "chamando" ? "Cancelar ligação" : "Desligar"}
            className="h-10 w-10 rounded-full [&_svg]:size-5 sm:[&_svg]:size-5"
            onClick={onDesligar}
          >
            <PhoneOff aria-hidden />
          </Button>
        )}
      </div>

      {desktop && (
        <span id={idDaDica} className="sr-only">
          Setas movem a janela. Enter leva ao próximo canto.
        </span>
      )}
    </div>,
    document.body
  );
}
